import { People } from '@mui/icons-material';
import { Autocomplete, Box, Grid, TextField } from '@mui/material';
import { getTeamSummary } from 'lib/api/dashboard';
import TableCard from 'lib/designSystem/Card/TableCard';
import CentralPieChart from 'lib/designSystem/Charts/CentralPieChart';
import LineChart from 'lib/designSystem/Charts/LineChart';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import EnhancedTable from 'lib/designSystem/Table';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { BASE_TEAM_SUMMARY, ByUsersTable, MonthlyDataPoint } from './types';

const TOP_EXECUTIVES_COUNT = 5;

const processGraphDataForSelectedExecutives = (
  graphData: MonthlyDataPoint[],
  selectedExecutives: string[],
  allExecutives: string[],
): MonthlyDataPoint[] => {
  if (!graphData || graphData.length === 0) return graphData;

  const executiveNames = new Set<string>();
  graphData.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'name') executiveNames.add(key);
    });
  });

  const executivesToShow = new Set(selectedExecutives);

  const allExecutivesSelected = allExecutives.every((exec) =>
    executivesToShow.has(exec),
  );

  return graphData.map((item) => {
    const processedItem: MonthlyDataPoint = { name: item.name };

    executivesToShow.forEach((executiveName) => {
      const value = item[executiveName];
      if (value !== undefined) {
        processedItem[executiveName] = value;
      }
    });

    if (!allExecutivesSelected) {
      const otrosTotal = Array.from(executiveNames)
        .filter((name) => !executivesToShow.has(name))
        .reduce((sum, name) => {
          const value = item[name] as number;
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);

      if (otrosTotal > 0) {
        processedItem['Otros'] = otrosTotal;
      }
    }

    return processedItem;
  });
};

const TeamSummary = () => {
  const dispatch = useDispatch();
  const [cessionsSummary, setCessionsSummary] = useState(BASE_TEAM_SUMMARY);
  const [byUsersTable, setByUsersTable] = useState<ByUsersTable[]>([]);
  const [filteredChartData, setFilteredChartData] = useState<
    MonthlyDataPoint[]
  >([]);
  const [allExecutives, setAllExecutives] = useState<string[]>([]);
  const [selectedExecutives, setSelectedExecutives] = useState<string[]>([]);

  const { data, isPending, error } = getTeamSummary(dispatch);

  const processedPieChartData = useMemo(() => {
    const validData = cessionsSummary.summary.lastMonth.filter(
      (entry) =>
        entry &&
        typeof entry.name === 'string' &&
        typeof entry.value === 'number' &&
        entry.value > 0,
    );

    if (validData.length === 0 || selectedExecutives.length === 0) {
      return [];
    }

    const selectedSet = new Set(selectedExecutives);
    const selectedExecutivesData = validData.filter((entry) =>
      selectedSet.has(entry.name),
    );

    const otherExecutivesData = validData.filter(
      (entry) => !selectedSet.has(entry.name),
    );

    const result = selectedExecutivesData.sort((a, b) => b.value - a.value);

    if (otherExecutivesData.length > 0) {
      const otrosTotal = otherExecutivesData.reduce(
        (sum, entry) => sum + entry.value,
        0,
      );
      if (otrosTotal > 0) {
        result.push({
          name: 'Otros',
          value: otrosTotal,
        });
      }
    }

    return result;
  }, [cessionsSummary.summary.lastMonth, selectedExecutives]);

  const dataHeaders = useMemo(() => {
    return [
      { key: 'name', label: 'Ejecutivo' },
      ...(cessionsSummary.last6Months.map((x) => ({
        key: x,
        label: x,
        type: 'money',
      })) || []),
    ];
  }, [cessionsSummary.last6Months]);

  useEffect(() => {
    if (data) {
      setByUsersTable(data.byUsersTable);
      setCessionsSummary(data);

      const executiveNamesSet = new Set<string>();
      data?.graph.forEach((item: MonthlyDataPoint) => {
        Object.keys(item).forEach((key) => {
          if (key !== 'name') {
            executiveNamesSet.add(key);
          }
        });
      });
      const allExecutivesList = Array.from(executiveNamesSet);

      const executiveTotals = allExecutivesList.map((name) => {
        const total = data.graph.reduce((sum, item) => {
          const value = item[name] as number;
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);
        return { name, total };
      });

      const topExecutives = executiveTotals
        .sort((a, b) => b.total - a.total)
        .slice(0, TOP_EXECUTIVES_COUNT)
        .map((item) => item.name);

      setAllExecutives(allExecutivesList);
      setSelectedExecutives(topExecutives);
    }
    if (error) errorHandler(dispatch, error);
  }, [data, error, dispatch]);

  useEffect(() => {
    if (cessionsSummary.graph.length > 0) {
      if (selectedExecutives.length > 0) {
        const processedGraph = processGraphDataForSelectedExecutives(
          cessionsSummary.graph,
          selectedExecutives,
          allExecutives,
        );
        setFilteredChartData(processedGraph);
      } else {
        setFilteredChartData([]);
      }
    }
  }, [cessionsSummary, selectedExecutives, allExecutives]);

  if (isPending) return <CenteredCircularProgress type="layout" />;

  return (
    <Box marginTop={4}>
      <Title
        title="Resumen equipo"
        icon={<People color="primary" sx={{ fontSize: 20 }} />}
        color="primary"
      />
      <Grid container spacing={2}>
        <Grid item xs={8}>
          <TableCard>
            <SubtitleWithTooltip
              title="Selecciona ejecutivos adicionales"
              tooltipContent="Agrega o elimina ejecutivos específicos para visualizar en el gráfico. Inicialmente se muestran los top 5"
              tooltipPlacement="right"
              variant="compact"
            >
              <Autocomplete
                multiple
                disableCloseOnSelect
                limitTags={0}
                options={[
                  'TODOS_LOS_EJECUTIVOS',
                  'NINGUNO',
                  ...allExecutives.sort(),
                ]}
                value={selectedExecutives}
                onChange={(event, newValue) => {
                  if (newValue.includes('TODOS_LOS_EJECUTIVOS')) {
                    setSelectedExecutives(allExecutives);
                  } else if (newValue.includes('NINGUNO')) {
                    setSelectedExecutives([]);
                  } else {
                    const realExecutives = newValue.filter(
                      (name) =>
                        name !== 'TODOS_LOS_EJECUTIVOS' && name !== 'NINGUNO',
                    );
                    setSelectedExecutives(realExecutives);
                  }
                }}
                getOptionLabel={(option) =>
                  option === 'TODOS_LOS_EJECUTIVOS'
                    ? 'Todos los ejecutivos'
                    : option === 'NINGUNO'
                      ? 'Ninguno'
                      : option
                }
                isOptionEqualToValue={(option, value) => option === value}
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Buscar ejecutivos" />
                )}
                sx={{ width: 300, height: 40, mb: 1 }}
              />
            </SubtitleWithTooltip>
            <LineChart
              aspect={4.0 / 1.5}
              data={filteredChartData}
              showArea={false}
            />
          </TableCard>
        </Grid>
        <Grid item xs={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Este mes"
              tooltipContent="Aca podras ver el resumen de las cesiones de tus ejecutivos en el último mes."
              tooltipPlacement="right"
              variant="compact"
            ></SubtitleWithTooltip>
            <CentralPieChart data={processedPieChartData} showLegend={true} />
          </TableCard>
        </Grid>
        <Grid item xs={12}>
          <TableCard>
            <SubtitleWithTooltip
              title="Por ejecutivo"
              tooltipContent="Aca podras ver el desglose de las cesiones por ejecutivo por mes."
              tooltipPlacement="right"
              variant="compact"
            ></SubtitleWithTooltip>
            <EnhancedTable
              data={byUsersTable}
              dataHeaders={dataHeaders}
              finalSumRow
              exportToFile="xlsx"
            />
          </TableCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TeamSummary;
